import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { Checkbox } from '@wf-common/atoms'

import { isValidDate, moment } from '../../util/date'
import Tooltip from '../tooltip/tooltip'
import * as customPropTypes from './prop-types'
import TimeInput from './time-input'

import styles from './datepicker-popover-topbar.module.css'

const propTypes = {
    allDay: PropTypes.bool,
    allDayDisabled: PropTypes.bool,
    allDayLabel: PropTypes.string,
    allDayTooltipContent: PropTypes.string,
    autoAllDay: PropTypes.bool,
    emptyTimeField: PropTypes.bool,
    intl: customPropTypes.intl.isRequired,
    onResetTime: PropTypes.func,
    onTimeFrom: PropTypes.func,
    onTimeTo: PropTypes.func,
    rangeSelection: PropTypes.bool,
    timeFrom: PropTypes.string,
    timeFromError: PropTypes.instanceOf(Error),
    timeTo: PropTypes.string,
    timeToError: PropTypes.instanceOf(Error),
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), customPropTypes.dateRange])
}

const defaultProps = {
    allDay: false,
    allDayDisabled: false,
    allDayLabel: 'All-day',
    allDayTooltipContent: '',
    autoAllDay: true,
    emptyTimeField: false,
    onResetTime: () => {},
    onTimeFrom: () => {},
    onTimeTo: () => {},
    rangeSelection: false,
    timeFrom: '0:00',
    timeTo: '23:59',
    value: moment.range()
}

class DatePickerPopoverTopBar extends Component {
    constructor(props) {
        super(props)
        this.state = {
            timeSelectionEnabled: isTimeSelectionEnabled(props)
        }

        this.setTimeFrom = this.setTimeFrom.bind(this)
        this.setTimeTo = this.setTimeTo.bind(this)
        this.toggleTimeSelection = this.toggleTimeSelection.bind(this)
    }

    render() {
        return (
            <div className={cx('t3sel-top-bar', styles['top-bar'])}>
                {this.renderTimeInputs()}
                {this.renderCheckbox()}
            </div>
        )
    }

    renderTimeInputs() {
        const { timeSelectionEnabled } = this.state
        const { emptyTimeField, intl, rangeSelection, timeFrom, timeFromError, timeTo, timeToError, value } = this.props

        if (!timeSelectionEnabled) {
            return <div className={styles['inputs']} />
        }

        const visibleTimeFrom =
            timeFrom && timeFrom.length
                ? timeFrom
                : `${emptyTimeField ? '' : '00:00'}${
                      intl.formatTimeOnly(new Date()).match(/^.*?(AM|PM)$/i) ? ' AM' : ''
                  }`

        return (
            <div className={styles['inputs']}>
                <TimeInput
                    dateValue={isValidDate(value.start) ? value.start : null}
                    timeValue={visibleTimeFrom}
                    error={timeFromError}
                    onChange={this.setTimeFrom}
                    allowEmpty={emptyTimeField}
                    {...{ intl }}
                />
                {rangeSelection && <span>–</span>}
                {rangeSelection && (
                    <TimeInput
                        dateValue={value.end}
                        timeValue={timeTo}
                        allowEmpty={emptyTimeField}
                        error={timeToError}
                        onChange={this.setTimeTo}
                        {...{ intl }}
                    />
                )}
            </div>
        )
    }

    renderCheckbox() {
        const { allDayDisabled, allDayLabel } = this.props
        const { timeSelectionEnabled } = this.state

        const renderedAllDayCheckbox = (
            <Checkbox
                className={cx('t3sel-datepicker-time-toggle', styles['all-day-toggle'])}
                label={allDayLabel}
                checked={!timeSelectionEnabled || allDayDisabled}
                onChange={this.toggleTimeSelection}
                disabled={allDayDisabled}
            />
        )

        return this.renderTooltip(renderedAllDayCheckbox)
    }

    renderTooltip(renderedAllDayCheckbox) {
        const { allDayTooltipContent } = this.props

        return allDayTooltipContent ? (
            <Tooltip content={allDayTooltipContent} placement="left">
                <div>{renderedAllDayCheckbox}</div>
            </Tooltip>
        ) : (
            renderedAllDayCheckbox
        )
    }

    setTimeFrom(timeFrom) {
        const { onTimeFrom } = this.props
        onTimeFrom(timeFrom)
    }

    setTimeTo(timeTo) {
        const { onTimeTo } = this.props
        onTimeTo(timeTo)
    }

    toggleTimeSelection() {
        const { onResetTime } = this.props
        const { timeSelectionEnabled } = this.state

        this.setState({
            timeSelectionEnabled: !timeSelectionEnabled
        })

        onResetTime(timeSelectionEnabled)
    }
}

DatePickerPopoverTopBar.propTypes = propTypes
DatePickerPopoverTopBar.defaultProps = defaultProps

export default DatePickerPopoverTopBar

export function isTimeSelectionEnabled(props) {
    const { allDay, allDayDisabled, autoAllDay } = props
    return !allDayDisabled && ((!autoAllDay && !allDay) || (autoAllDay && !isWholeDay(props)))
}

function isWholeDay(props) {
    const { intl, rangeSelection, timeFrom, timeTo } = props
    const startOfDay = intl.formatTimeOnly(moment().startOf('day'))
    const endOfDay = intl.formatTimeOnly(moment().endOf('day'))
    const isStartOfDay = timeFrom === startOfDay
    const isEndOfDay = timeTo === endOfDay
    return isStartOfDay && (isEndOfDay || !rangeSelection)
}
